import React, { useMemo } from "react";
import { useTranslation } from "../../../i18n";
import DeletionCaseReviewCard from "./DeletionCaseReviewCard";
import DeletionCaseReviewPreviewStrip from "./DeletionCaseReviewPreviewStrip";
import { normalizeDeletionCaseReviewItems } from "../../utils/deletionCaseReviews";

/**
 * Structured panel listing all reviews linked to a deletion case.
 * @param {{
 *   items: object[];
 *   variant?: 'panel' | 'embedded';
 *   showPreviewStrip?: boolean;
 *   onViewAllFromStrip?: () => void;
 *   className?: string;
 * }} props
 */
const DeletionCaseReviewsSection = ({
  items: rawItems,
  variant = "panel",
  showPreviewStrip = false,
  onViewAllFromStrip,
  showOutcomeBadges = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const items = useMemo(
    () => normalizeDeletionCaseReviewItems(rawItems),
    [rawItems]
  );

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#64748b]">
          {t("deletionCaseUi.reviewsForDeletion")}
        </h3>
        <p className="mt-0.5 text-[12px] text-[#94a3b8]">
          {t("deletionCaseUi.reviewsInCase", { count: items.length })}
        </p>
      </div>
      {showPreviewStrip && items.length > 0 ? (
        <DeletionCaseReviewPreviewStrip
          items={items}
          onViewAll={onViewAllFromStrip}
          className="max-w-full justify-end"
        />
      ) : null}
    </div>
  );

  if (items.length === 0) {
    return (
      <section
        className={className}
        aria-label={t("deletionCaseUi.reviewsForDeletion")}
      >
        {header}
        <p className="mt-3 rounded-lg border border-dashed border-[#e2e8f0] bg-[#fafbfc] px-4 py-6 text-center text-[13px] text-[#64748b]">
          {t("deletionCaseUi.noLinkedReviews")}
        </p>
      </section>
    );
  }

  const gridClassName =
    variant === "panel"
      ? "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      : "mt-4 grid grid-cols-1 gap-3 md:grid-cols-2";

  const grid = (
    <ul className={gridClassName}>
      {items.map((review, index) => (
        <li key={review.id ?? `${review.reviewId}-${index}`}>
          <DeletionCaseReviewCard
            review={review}
            index={index}
            showOutcomeBadge={showOutcomeBadges}
          />
        </li>
      ))}
    </ul>
  );

  if (variant === "embedded") {
    return (
      <section
        className={className}
        aria-label={t("deletionCaseUi.reviewsForDeletion")}
      >
        {header}
        {grid}
      </section>
    );
  }

  return (
    <section
      className={`rounded-[12px] border border-[#e8ecf0] bg-[#fafbfc] p-4 md:p-5 ${className}`}
      aria-label={t("deletionCaseUi.reviewsForDeletion")}
    >
      {header}
      {grid}
    </section>
  );
};

export default DeletionCaseReviewsSection;
